import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { ApiError, type MaterialRow, type ShortageReason, type ShortageRow, type ShortageUrgency } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { hq } from '../api/client';
import { TipText } from '@hq/ui-kit';

interface ShortageFormValues {
  /** 目录内商品 ID；'0' = 目录外手工填写（哨兵值必须是字符串，数字 0 会丢 19 位 id 精度）。 */
  materialId?: string;
  /** 命中目录商品时快照的主数据编码/品名（手工填写为空）。 */
  materialCode?: string;
  materialName?: string;
  materialNameReq: string;
  specReq?: string;
  brandReq?: string;
  reqQty: number;
  unitName?: string;
  expectPrice?: number;
  expectDate?: dayjs.Dayjs;
  urgency: ShortageUrgency;
  reasonType: ShortageReason;
  remark?: string;
}

const URGENCY_META: Record<string, { color: string; text: string }> = {
  NORMAL: { color: 'default', text: '普通' },
  URGENT: { color: 'orange', text: '加急' },
  EMERGENCY: { color: 'red', text: '应急' }
};

const REASON_META: Record<string, string> = {
  STOCK_OUT: '缺货',
  NOT_IN_CATALOG: '目录无此商品',
  PRICE_HIGH: '价格偏高',
  QUALITY: '质量问题',
  OTHER: '其他'
};

const STATUS_META: Record<string, { color: string; text: string }> = {
  SUBMITTED: { color: 'blue', text: '待处理' },
  AUDITING: { color: 'gold', text: '处理中' },
  HANDLED: { color: 'green', text: '已处理' },
  REJECTED: { color: 'default', text: '已驳回' },
  CLOSED: { color: 'default', text: '已关闭' }
};

const HANDLE_META: Record<string, string> = {
  ADD_CATALOG: '已加目录',
  SUBSTITUTE: '替代商品',
  PURCHASE: '转采购',
  REJECT: '驳回'
};

/**
 * 商品名称：先搜目录内已有物资（命中后带出规格/品牌/单位并关联 materialId），
 * 目录无此商品时可直接手工填写（此时 materialId 归 0）。
 */
function MaterialSearchInput({
  value,
  onChange,
  onPick
}: {
  value?: string;
  onChange?: (value: string) => void;
  onPick?: (material?: MaterialRow) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const [pickedName, setPickedName] = useState<string | null>(null);
  const search = useQuery({
    queryKey: ['shortage', 'material-search', keyword],
    queryFn: () => hq.material.list({ keyword: keyword || undefined, size: 20 })
  });
  const rows = search.data?.items ?? [];
  return (
    <AutoComplete
      value={value}
      placeholder="搜索名称 / 编码，目录无此商品时可直接填写"
      allowClear
      onSearch={setKeyword}
      onChange={(next) => {
        const text = typeof next === 'string' ? next : '';
        onChange?.(text);
        // 文本偏离已选中项即解除关联（随后 onSelect 命中会再回填，两种触发顺序均收敛）
        if (text !== pickedName) onPick?.(undefined);
      }}
      onSelect={(next) => {
        setPickedName(next);
        onPick?.(rows.find((row) => (row.materialName ?? '') === next));
      }}
      options={rows.map((row) => ({
        value: row.materialName ?? '',
        label: `${row.materialName ?? ''}${row.materialCode ? `（${row.materialCode}）` : ''}${row.spec ? ` · ${row.spec}` : ''}`
      }))}
    />
  );
}

/**
 * 缺货商品在线申请（招标条款 (2)-7）：科室提交 → 运营在响应时限内处理。
 * 列表按本科室隔离（后端按 JWT dept_id 过滤）。
 */
export default function ShortagePage() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<ShortageFormValues>();

  const list = useQuery({
    queryKey: ['shortage', 'mine'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.shortage.list({ size: 50 })
  });

  const create = useMutation({
    mutationFn: (values: ShortageFormValues) =>
      hq.shortage.create({
        materialId: values.materialId ?? '0',
        materialCode: values.materialCode,
        materialName: values.materialName,
        materialNameReq: values.materialNameReq,
        specReq: values.specReq,
        brandReq: values.brandReq,
        reqQty: values.reqQty,
        unitName: values.unitName,
        expectPrice: values.expectPrice ?? 0,
        expectDate: values.expectDate ? values.expectDate.format('YYYY-MM-DD') : undefined,
        urgency: values.urgency,
        reasonType: values.reasonType,
        remark: values.remark
      }),
    onSuccess: () => {
      message.success('缺货申请已提交，运营将在响应时限内处理');
      setOpen(false);
      form.resetFields();
      void queryClient.invalidateQueries({ queryKey: ['shortage', 'mine'] });
    }
  });

  const rows = list.data?.items ?? [];
  const error = list.error instanceof ApiError ? list.error.friendlyMessage : null;

  /** 命中目录物资则关联 ID 并快照品名/编码/规格/品牌/单位；手工填写（undefined）则解除关联。 */
  const handlePick = (material?: MaterialRow) => {
    if (material) {
      form.setFieldsValue({
        materialId: material.id,
        materialCode: material.materialCode ?? undefined,
        materialName: material.materialName ?? undefined,
        specReq: material.spec ?? undefined,
        brandReq: material.brand ?? undefined,
        unitName: material.unitName ?? undefined
      });
    } else {
      form.setFieldsValue({ materialId: '0', materialCode: undefined, materialName: undefined });
    }
  };

  const columns: ColumnsType<ShortageRow> = [
    { title: '单号', dataIndex: 'bizNo', width: 190 },
    {
      title: '商品',
      ellipsis: { showTitle: false },
      // 单行展示：规格收进悬浮提示，避免行高被拉高
      render: (_, row) => {
        const name = row.materialName ?? row.materialNameReq ?? '-';
        return <TipText text={name} tip={row.specReq ? `${name} · ${row.specReq}` : undefined} />;
      }
    },
    {
      title: '需求数量',
      dataIndex: 'reqQty',
      width: 110,
      align: 'right',
      render: (value: number, row) => `${value} ${row.unitName ?? ''}`
    },
    {
      title: '紧急度',
      dataIndex: 'urgency',
      width: 90,
      render: (value: string) => <Tag color={URGENCY_META[value]?.color}>{URGENCY_META[value]?.text ?? value}</Tag>
    },
    {
      title: '缺货原因',
      dataIndex: 'reasonType',
      width: 130,
      render: (value: string) => REASON_META[value] ?? value
    },
    {
      title: '状态',
      dataIndex: 'auditStatus',
      width: 100,
      render: (value: string) => <Tag color={STATUS_META[value]?.color}>{STATUS_META[value]?.text ?? value}</Tag>
    },
    {
      title: '响应时限',
      dataIndex: 'respondDeadline',
      width: 170,
      render: (value: string | null, row) => {
        if (!value) return '-';
        const overdue = !row.respondAt && dayjs(value).isBefore(dayjs());
        return (
          <Typography.Text type={overdue ? 'danger' : undefined}>
            {formatDisplayTime(value)}
            {overdue ? '（超时）' : ''}
          </Typography.Text>
        );
      }
    },
    {
      title: '处理结果',
      dataIndex: 'handleResult',
      width: 120,
      render: (value: string | null) =>
        value ? <Tag color="blue">{HANDLE_META[value] ?? value}</Tag> : <Typography.Text type="secondary">-</Typography.Text>
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          缺货申请
          <Typography.Text type="secondary">目录缺货 / 目录无此商品时提交，运营在时限内响应</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            提交缺货申请
          </Button>
        </Space>
      }
    >
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message="缺货申请加载失败" description={error} /> : null}

      <Table<ShortageRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        locale={{ emptyText: '暂无缺货申请' }}
        expandable={{
          expandedRowRender: (row) => (
            <Space direction="vertical" size={4}>
              <Typography.Text>缺货原因：{REASON_META[row.reasonType] ?? row.reasonType}</Typography.Text>
              <Typography.Text>期望单价：{row.expectPrice > 0 ? `${row.expectPrice} 元` : '未指定'}</Typography.Text>
              <Typography.Text>提交时间：{formatDisplayTime(row.bizDate)}</Typography.Text>
              {row.respondAt ? <Typography.Text>处理时间：{formatDisplayTime(row.respondAt)}</Typography.Text> : null}
              {row.remark ? <Typography.Text type="secondary">备注：{row.remark}</Typography.Text> : null}
            </Space>
          )
        }}
      />

      <Modal
        title="提交缺货申请"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        destroyOnHidden
      >
        <Form<ShortageFormValues>
          form={form}
          layout="vertical"
          initialValues={{ urgency: 'NORMAL', reasonType: 'STOCK_OUT', reqQty: 1 }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="materialId" hidden initialValue="0">
            <Input />
          </Form.Item>
          <Form.Item name="materialCode" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="materialName" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="materialNameReq" label="商品名称" rules={[{ required: true, message: '请填写需要的商品名称' }]}>
            <MaterialSearchInput onPick={handlePick} />
          </Form.Item>
          <Form.Item name="specReq" label="规格">
            <Input placeholder="如：5ml 带针" maxLength={255} />
          </Form.Item>
          <Form.Item name="brandReq" label="品牌要求">
            <Input placeholder="选填" maxLength={128} />
          </Form.Item>
          <Space size={12} style={{ display: 'flex' }}>
            <Form.Item
              name="reqQty"
              label="需求数量"
              rules={[{ required: true, message: '请填写数量' }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0.001} step={1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="unitName" label="单位" style={{ flex: 1 }}>
              <Input placeholder="如：支" maxLength={32} />
            </Form.Item>
          </Space>
          <Space size={12} style={{ display: 'flex' }}>
            <Form.Item name="urgency" label="紧急度" style={{ flex: 1 }}>
              <Select
                popupMatchSelectWidth={false}
                options={[
                  { value: 'NORMAL', label: '普通（24h 响应）' },
                  { value: 'URGENT', label: '加急（4h 响应）' },
                  { value: 'EMERGENCY', label: '应急（2h 响应）' }
                ]}
              />
            </Form.Item>
            <Form.Item name="reasonType" label="缺货原因" style={{ flex: 1 }}>
              <Select
                popupMatchSelectWidth={false}
                options={[
                  { value: 'STOCK_OUT', label: '缺货' },
                  { value: 'NOT_IN_CATALOG', label: '目录无此商品' },
                  { value: 'PRICE_HIGH', label: '价格偏高' },
                  { value: 'QUALITY', label: '质量问题' },
                  { value: 'OTHER', label: '其他' }
                ]}
              />
            </Form.Item>
          </Space>
          <Space size={12} style={{ display: 'flex' }}>
            <Form.Item name="expectPrice" label="期望单价（元）" style={{ flex: 1 }}>
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="expectDate" label="希望到货日" style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
